import React, { useEffect, useState } from "react";
import { Col, Container, Row, Table } from "react-bootstrap";
import { CSVLink } from "react-csv";
import { useLoaderData } from "react-router-dom";
import Select from "react-select";

import { IPlacement } from "../Clubhouse/components/Placement/types/placement";

import { store } from "../..";
import {
    globalActions,
    selectSetting,
} from "../../features/global/globalSlice";
import { leagueManagementActions } from "../../features/leaguemgmt/leagueManagementSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { approveCOI, getPlacementResults } from "../../lib/api";
import { DownArrowIcon, UpArrowIcon } from "../../lib/icons";
import { IAPIResponse } from "../../types/api";

interface Filters {
    ageLevels: {
        options: Array<string>;
        filter: string | undefined;
    };
    year: {
        options: Array<number>;
        filter: number | undefined;
    };
}

interface CSVData {
    team: string;
    age: string;
    bottomquarter: number;
    bottomhalf: number;
    tophalf: number;
    topquarter: number;
    votes: number;
    average: number;
}

type SortOrderType = Partial<
    Record<"team" | "age" | "average", "asc" | "desc">
>;

export const loader = async () => {
    const years = store.getState().leaguemanagement.years;
    const settings = store.getState().global.settings;

    const promises = [];

    !years && promises.push(store.dispatch(leagueManagementActions.getYears()));

    !settings.length &&
        promises.push(store.dispatch(globalActions.getSettings()));

    await Promise.all(promises);

    return {
        header: "Placement Results",
    };
};

const PlacementResults = (): React.JSX.Element | undefined => {
    const dispatch = useAppDispatch();
    const { years } = useAppSelector(state => state.leaguemanagement);
    const currentYear = useAppSelector(state =>
        selectSetting(state, "majorybl_current_league_year"),
    );

    const [placements, setPlacements] = useState<IPlacement[]>([]);
    const [csvData, setCSVData] = useState<Array<CSVData>>([]);
    const [sortOrder, setSortOrder] = useState<SortOrderType>({
        age: "asc",
    });

    const [filters, setFilters] = useState<Filters>({
        ageLevels: {
            options: [],
            filter: undefined,
        },
        year: {
            options: years
                ? Array.from(Object.keys(years)).map(y => parseInt(y))
                : [],
            filter: parseInt(currentYear?.setting) || undefined,
        },
    });

    useEffect(() => {
        setFilters(prev => ({
            ...prev,
            ageLevels: {
                ...prev.ageLevels,
                options: Array.from(
                    new Set(placements.map(p => p.team.ageLevel)),
                ).sort((a, b) => {
                    const ageLevelA = parseInt(a.match(/\d+/)?.[0] || "0");
                    const ageLevelB = parseInt(b.match(/\d+/)?.[0] || "0");

                    return ageLevelA - ageLevelB;
                }) as string[],
            },
        }));
    }, [placements]);

    useEffect(() => {
        const fetchPlacements = async () => {
            const { placements } = await getPlacementResults(
                filters.year.filter,
            );
            setPlacements(placements);
        };

        fetchPlacements();
    }, [filters.year.filter]);

    placements.sort((a, b) => {
        const ageLevelComparison =
            (a.team.ageLevel ? parseInt(a.team.ageLevel) : 0) -
            (b.team.ageLevel ? parseInt(b.team.ageLevel) : 0);

        if (ageLevelComparison !== 0) {
            return ageLevelComparison;
        }

        return a.team.name.localeCompare(b.team.name);
    });

    const CSVHeaders = [
        { label: "Team Name", key: "team" },
        { label: "Age Level", key: "age" },
        { label: "Bottom 1/4", key: "bottomquarter" },
        { label: "Bottom 1/2", key: "bottomhalf" },
        { label: "Top 1/2", key: "tophalf" },
        { label: "Top 1/4", key: "topquarter" },
        { label: "Total Votes", key: "votes" },
        { label: "Average", key: "average" },
    ];

    const handleDownload = () => {
        const filteredData = placements
            .filter(p =>
                filters.ageLevels.filter
                    ? filters.ageLevels.filter.includes(p.team.ageLevel)
                    : true,
            )
            .map(p => {
                return {
                    team: p.team.name,
                    age: p.team.ageLevel,
                    bottomquarter: p.totals[1],
                    bottomhalf: p.totals[2],
                    tophalf: p.totals[3],
                    topquarter: p.totals[4],
                    votes: p.totals.ratings,
                    average: p.totals.average,
                };
            })
            .filter((data): data is CSVData => data !== null);

        setCSVData(filteredData);
    };

    const handleSortClick = (key: "team" | "age" | "average") => {
        setSortOrder(prevSortOrder => {
            const newSortOrder: SortOrderType = {};

            if (prevSortOrder) {
                if (prevSortOrder[key] === "asc") {
                    newSortOrder[key] = "desc";
                } else if (prevSortOrder[key] === "desc") {
                    newSortOrder[key] = undefined;
                } else {
                    newSortOrder[key] = "asc";
                }
            } else {
                newSortOrder[key] = "asc";
            }

            return newSortOrder;
        });
    };

    return (
        <Container className="placementResultsContent" fluid="xs">
            <h2>Placement Results</h2>
            <Row className="placementResultsFilters">
                <Col sm={3}>
                    <h5>Age Level:</h5>
                    <Select
                        isSearchable={false}
                        isClearable={true}
                        isMulti={false}
                        options={filters.ageLevels.options.map((a, idx) => {
                            return {
                                value: idx,
                                label: a,
                            };
                        })}
                        onChange={a => {
                            setFilters(prev => ({
                                ...prev,
                                ageLevels: {
                                    ...prev.ageLevels,
                                    filter: a ? a.label : undefined,
                                },
                            }));
                        }}
                    />
                </Col>
                <Col sm={3}>
                    <h5>League Year:</h5>
                    <Select
                        isSearchable={false}
                        isClearable={true}
                        isMulti={false}
                        options={filters.year.options.map(y => {
                            return {
                                value: y,
                                label: y,
                            };
                        })}
                        defaultValue={
                            filters.year.filter
                                ? {
                                      value: filters.year.filter,
                                      label: filters.year.filter,
                                  }
                                : undefined
                        }
                        onChange={y => {
                            setFilters(prev => ({
                                ...prev,
                                year: {
                                    ...prev.year,
                                    filter: y?.value,
                                },
                            }));
                        }}
                    />
                </Col>
            </Row>
            <Row>
                <Col className="d-flex justify-content-end">
                    <CSVLink
                        data={csvData}
                        headers={CSVHeaders}
                        filename={`placement-results.${new Date().getTime()}.csv`}
                        className="btn btn-primary pull-right"
                        target="_blank"
                        onClick={handleDownload}
                    >
                        Export Placement Results
                    </CSVLink>
                </Col>
            </Row>
            <Table className="leaguesTable" striped bordered responsive>
                <thead>
                    <tr>
                        <th
                            onClick={() => handleSortClick("team")}
                            data-sortable="true"
                        >
                            Team Name{" "}
                            {sortOrder?.team === "asc" ? (
                                <UpArrowIcon className="icon mx-1" />
                            ) : sortOrder?.team === "desc" ? (
                                <DownArrowIcon className="icon mx-1" />
                            ) : (
                                ""
                            )}
                        </th>
                        <th
                            onClick={() => handleSortClick("age")}
                            data-sortable="true"
                        >
                            Age{" "}
                            {sortOrder?.age === "asc" ? (
                                <UpArrowIcon className="icon mx-1" />
                            ) : sortOrder?.age === "desc" ? (
                                <DownArrowIcon className="icon mx-1" />
                            ) : (
                                ""
                            )}
                        </th>
                        <th>Bottom 1/4</th>
                        <th>Bottom 1/2</th>
                        <th>Top 1/2</th>
                        <th>Top 1/4</th>
                        <th>Total Votes</th>
                        <th
                            onClick={() => handleSortClick("average")}
                            data-sortable="true"
                        >
                            Average{" "}
                            {sortOrder?.average === "asc" ? (
                                <UpArrowIcon className="icon mx-1" />
                            ) : sortOrder?.average === "desc" ? (
                                <DownArrowIcon className="icon mx-1" />
                            ) : (
                                ""
                            )}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {placements
                        .filter(p =>
                            filters.ageLevels.filter
                                ? filters.ageLevels.filter.includes(
                                      p.team.ageLevel,
                                  )
                                : true,
                        )
                        .sort((a, b) => {
                            if (sortOrder) {
                                let aValue: any;
                                let bValue: any;
                                const sortKey = Object.keys(
                                    sortOrder,
                                )[0] as keyof SortOrderType;

                                switch (sortKey) {
                                    case "age":
                                        aValue = parseInt(a.team.ageLevel);
                                        bValue = parseInt(b.team.ageLevel);
                                        break;
                                    case "average":
                                        aValue = a.totals.average;
                                        bValue = b.totals.average;
                                        break;
                                    case "team":
                                        aValue = a.team.name;
                                        bValue = b.team.name;
                                        break;
                                }

                                if (sortOrder[sortKey] === "asc") {
                                    return aValue > bValue ? 1 : -1;
                                } else {
                                    return aValue < bValue ? 1 : -1;
                                }
                            } else {
                                return 0;
                            }
                        })
                        .map((p, idx) => (
                            <tr key={idx}>
                                <td>{p.team.name}</td>
                                <td>{p.team.ageLevel}</td>
                                <td>{p.totals[1]}</td>
                                <td>{p.totals[2]}</td>
                                <td>{p.totals[3]}</td>
                                <td>{p.totals[4]}</td>
                                <td>{p.totals.ratings}</td>
                                <td>{p.totals.average}</td>
                            </tr>
                        ))}
                </tbody>
            </Table>
        </Container>
    );
};

export default PlacementResults;
